import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  DashboardAclRole,
  DashboardWidgetCategory,
} from '@tracxtms/shared';

export type DashboardCatalogKind = 'builtin' | 'shared';

export interface DashboardCatalogEntry {
  id: string;
  label: string;
  kind: DashboardCatalogKind;
  icon?: string;
}

export interface DashboardWidgetCatalogEntry {
  id: string;
  label: string;
  category: DashboardWidgetCategory;
}

export interface DashboardPermissionsResponse {
  roles: DashboardAclRole[];
  dashboards: DashboardCatalogEntry[];
  widgets: DashboardWidgetCatalogEntry[];
  matrix: {
    dashboards: Record<string, string[]>;
    widgets: Record<string, string[]>;
  };
  hasOverrides: boolean;
}

const QUERY_KEY = ['dashboard-permissions'] as const;

export function useDashboardPermissions() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<{ data: DashboardPermissionsResponse }>(
        '/settings/dashboard-permissions',
      );
      return res.data.data;
    },
  });
}

export interface SaveDashboardPermissionsInput {
  dashboards: Record<string, string[]>;
  widgets: Record<string, string[]>;
}

export function useSaveDashboardPermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveDashboardPermissionsInput) => {
      const res = await api.put<{
        data: {
          matrix: { dashboards: Record<string, string[]>; widgets: Record<string, string[]> };
          hasOverrides: boolean;
        };
      }>('/settings/dashboard-permissions', input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // Stats + charts redaction reflects the new matrix
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'charts'] });
    },
  });
}
